import React, { useState } from "react";
import { CheckBox } from "./CheckBox";

interface MultiSelectBoxProps {
  label?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
}

export default function MultiSelectBox({
  label,
  values,
  onChange,
  options,
  placeholder = "선택",
  className = "",
  disabled = false,
  required = false,
  invalid = false,
}: MultiSelectBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasLabel = label !== undefined;

  const handleOptionToggle = (optionValue: string) => {
    const newValues = values.includes(optionValue)
      ? values.filter(val => val !== optionValue)
      : [...values, optionValue];
    onChange(newValues);
  };

  const selectedOptions = options.filter(option => values.includes(option.value));

  return (
    <div className={`relative ${className}`}>
      {/* Label - 속성 존재 시에만 영역 생성 */}
      {hasLabel && (
        <div className="h-6 mb-1">
          <label className="block text-base font-normal text-[#414651]">
            {required && <span className="text-red-500 mr-1">*</span>}
            {label}
          </label>
        </div>
      )}

      {/* Select Box */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={`
            w-full bg-white border border-[#CCCCCC] rounded-md px-2 py-2 text-left
            focus:outline-none focus:ring-2 focus:ring-[#55b938] focus:border-[#55b938]
            ${disabled ? "bg-[#F6F6F6]! cursor-not-allowed" : "cursor-pointer hover:border-gray-400"}
            flex items-center justify-between
            ${invalid ? "border-[#FF3232]" : "border-[#CCCCCC]"}
          `}
        >
          <span className={selectedOptions.length > 0 ? "text-[#333333]" : "text-[#717680]"}>
            {selectedOptions.length > 0 ? `${selectedOptions.length}개 선택됨` : placeholder}
          </span>

          {/* Arrow Icon */}
          <img
            src="/icons/headerArrowBlack.svg"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-[20px] w-[20px]"
          />
        </button>

        {/* Dropdown Options */}
        {isOpen && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map(option => (
              <div
                key={option.value}
                className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleOptionToggle(option.value)}
              >
                <CheckBox checked={values.includes(option.value)} onChange={() => handleOptionToggle(option.value)} />
                <span className="ml-2 text-gray-900">{option.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Backdrop to close dropdown */}
      {isOpen && <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />}
    </div>
  );
}
